import React, { useState, useEffect } from "react";
import { auth, db, ref, get } from "./firebase";
import { useAuth } from "./AuthContext";
import { FiUser, FiMail, FiBook, FiEdit2 } from "react-icons/fi";

const Profile = () => {
  const [user, setUser] = useState({
    name: "",
    email: "",
    studentId: "",
    level: ""
  });
  const [initials, setInitials] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrolledCourse, setEnrolledCourse] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  foundUser.enrolledCourse = courseId; // Add course ID to user data
                  break;
                }
              }
            }
          }
        }

        if (foundUser) {
          const fullName = foundUser.name || "";
          const nameParts = fullName.split(" ");
          const firstName = nameParts[0] || "";
          const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

          setUser({
            name: foundUser.name || "N/A",
            email: foundUser.email || "N/A",
            studentId: foundUser.studentId || "N/A",
            level: foundUser.enrolledCourse || "N/A"
          });

          const initials = `${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ""}`;
          setInitials(initials.toUpperCase());

          if (foundUser.enrolledCourse) {
            setEnrolledCourse(foundUser.enrolledCourse);
          }
        } else {
          setError("User data not found. Please contact support.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
          <div className="text-red-400 font-medium text-lg mb-2">Error</div>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="relative">
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center text-4xl md:text-5xl font-bold text-white shadow-lg">
              {initials}
            </div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-gray-900"></div>
          </div>
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{user.name}</h1>
            <p className="text-teal-100 text-base md:text-lg">{user.level}</p>
          </div>
        </div>
      </div>

      {/* Profile Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Personal Information */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <FiUser className="mr-2 text-teal-400" />
            Personal Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
              <span className="text-gray-400">Full Name</span>
              <span className="text-white font-medium">{user.name}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
              <span className="text-gray-400">Email Address</span>
              <span className="text-white font-medium break-all">{user.email}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
              <span className="text-gray-400">Student ID</span>
              <span className="text-white font-medium font-mono">{user.studentId}</span>
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
            <FiBook className="mr-2 text-teal-400" />
            Academic Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
              <span className="text-gray-400">Current Level</span>
              <span className="text-white font-medium">{user.level}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
              <span className="text-gray-400">Status</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                Active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;